import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as IconBell } from './../../images/icons/icon-bell.svg'
import {
  useGetUserNotifications,
  type UserNotification,
} from '../../api/notifications/useGetUserNotifications'
import { ReactComponent as IconClose } from '../../images/icons/icon_close.svg'
import { ReactComponent as IconCustomers } from '../../images/icons/notifications/icon-notifications-customers.svg'
import { ReactComponent as IconAudience } from '../../images/icons/notifications/icon-notifications-audience.svg'
import { ReactComponent as IconInventory } from '../../images/icons/notifications/icon-notifications-inventory.svg'
import { ReactComponent as IconReports } from '../../images/icons/notifications/icon-notifications-reports.svg'
import classNames from 'classnames'
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from '@floating-ui/react'
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'
import { usePostReadNotificationById } from '../../api/notifications/usePostReadNotificationById'
import { useNavigate } from 'react-router-dom'
import { usePostReadAllNotifications } from '../../api/notifications/usePostReadAllNotifications'
import { match } from 'ts-pattern'
import toast from 'react-hot-toast'
import NotificationToast from './NotificationToast'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../store/selectors/userSelector'
dayjs.extend(relativeTime)

export default function NotificationsBell(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const user = useSelector(currentUserSelector)
  const isBetaTester = useMemo(() => user?.beta_tester, [user?.beta_tester])
  const navigate = useNavigate()
  const {
    data: paginatedResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingNotifications,
  } = useGetUserNotifications()
  const { mutateAsync: markNotificationAsRead } = usePostReadNotificationById()
  const { mutate: markAllNotificationsAsRead } = usePostReadAllNotifications()

  const lastNotification = useRef<UserNotification | undefined>(undefined)
  const isFirstLoad = useRef(true)

  useEffect(() => {
    const firstPage = paginatedResponse?.pages[0]
    if (isBetaTester && firstPage && (firstPage?.data?.length ?? 0) > 0) {
      const currentFirstNotification = firstPage.data[0]

      // Only show the toast if it's not the first load and the notification has changed.
      if (
        !isFirstLoad.current &&
        lastNotification.current?._id !== currentFirstNotification._id
      ) {
        toast.custom(
          (t) => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'}`}>
              <NotificationToast
                notification={currentFirstNotification}
                onClose={() => {
                  toast.dismiss(t.id)
                  markNotificationAsRead({
                    data: {
                      notificationId: currentFirstNotification._id.toString(),
                    },
                  })
                }}
              />
            </div>
          ),
          {
            position: 'bottom-right',
          },
        )
      }

      // Update the stored notification
      lastNotification.current = currentFirstNotification

      // Mark the initial load as complete
      if (isFirstLoad.current) {
        isFirstLoad.current = false
      }
    }
  }, [isBetaTester, markNotificationAsRead, paginatedResponse])

  const firstPageNotifications = useMemo(
    () => paginatedResponse?.pages[0]?.data || [],
    [paginatedResponse],
  )

  // Check if there's at least one notification without a "readAt" property
  const hasUnreadNotification = useMemo(
    () => firstPageNotifications.some((notification) => !notification.readAt),
    [firstPageNotifications],
  )

  const arrowRef = useRef(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { refs, strategy, x, y, context } = useFloating({
    open: isOpen,
    placement: 'right-start',
    onOpenChange: setIsOpen,
    middleware: [offset(28), arrow({ element: arrowRef, padding: 4 })],
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useFocus(context),
    useDismiss(context),
  ])
  const onIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  React.useEffect(() => {
    const observer = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    })

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [onIntersect])

  const handleMarkAllAsRead = (e) => {
    e.preventDefault()
    markAllNotificationsAsRead()
  }

  const NotificationIcon = ({ notification }: { notification: UserNotification }) => {
    const IconComponent = match(notification.category)
      .with('customers', () => IconCustomers)
      .with('audience', () => IconAudience)
      .with('inventory', () => IconInventory)
      .with('reports', () => IconReports)
      .otherwise(() => IconReports)

    return (
      <div className="flex justify-center items-center bg-primary-glaucous/20 rounded-full p-2">
        <IconComponent />
      </div>
    )
  }

  return (
    <div>
      <div
        ref={refs.setReference}
        className="w-10 h-10 bg-primary-independence items-center flex justify-center rounded-full relative cursor-pointer"
        {...getReferenceProps()}>
        <IconBell />
        {hasUnreadNotification && (
          <div className="absolute top-0 right-0 w-3 h-3 bg-[#EB5757] rounded-full" />
        )}
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps({ style: { top: y ?? '', left: x ?? '' } })}
          className="absolute bg-blue-primary rounded-lg w-[460px] h-[360px] flex flex-col"
          {...{ x, y, strategy }}>
          <div className="flex flex-row justify-between items-center border-b border-web-gray/20 px-4 py-4">
            <div className="flex gap-4 items-center">
              <IconBell />
              <h4 className="font-bold text-md text-white">Notifications</h4>
            </div>
            <IconClose
              stroke="white"
              className="cursor-pointer"
              onClick={() => setIsOpen(false)}
            />
          </div>
          <div className="flex flex-col w-full h-full overflow-scroll">
            {firstPageNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-white text-sm font-bold">
                  {isLoadingNotifications ? 'Loading...' : 'No notifications found'}
                </p>
              </div>
            ) : (
              paginatedResponse?.pages.map((page, pageIndex) => {
                return (
                  <React.Fragment key={pageIndex}>
                    {page.data.map((notification) => {
                      return (
                        <div
                          onClick={(e) => {
                            e.preventDefault()
                            if (notification.meta?.path) {
                              navigate(notification.meta.path)
                              setIsOpen(false)
                            }
                          }}
                          onMouseOver={(e) => {
                            if (notification.readAt) return
                            markNotificationAsRead({
                              data: {
                                notificationId: notification._id.toString(),
                              },
                            })
                          }}
                          className={classNames(
                            'flex flex-row group justify-between items-center border-b border-web-gray/20 px-4 py-3 cursor-pointer hover:bg-primary-glaucous/10',
                            {
                              'bg-primary-glaucous/10': !notification.readAt,
                              'opacity-80': notification.readAt,
                            },
                          )}
                          key={notification._id.toString()}>
                          <div className="flex flex-row gap-3 max-w-[70%] items-center">
                            <NotificationIcon notification={notification} />
                            <div
                              className="flex flex-col gap-2 items-start"
                              onClick={(e) => {
                                setIsOpen(false)
                              }}>
                              <span className="font-bold text-xs text-web-gray text-white">
                                {notification.title}
                              </span>

                              <span className="font-bold text-xs text-web-gray text-primary-glaucous line-clamp-2">
                                {notification.message}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col h-full justify-end">
                            <div className="flex flex-col gap-1 items-end">
                              {!notification.readAt && (
                                <div className="w-2 h-2 bg-[#0DBDE3] rounded-full group-hover:bg-transparent" />
                              )}
                              <span className="font-bold text-xs group-hover:text-white">
                                {dayjs(notification.createdAt).fromNow()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </React.Fragment>
                )
              })
            )}
            <div ref={loadMoreRef} className="w-full py-2 text-center">
              {isFetchingNextPage && (
                <span className="text-white text-sm font-bold">Loading more...</span>
              )}
            </div>
          </div>
          <div className="flex flex-row justify-between items-center border-t border-web-gray/20 px-4 py-4">
            <h4
              className="font-bold text-xs hover:text-white cursor-pointer"
              onClick={handleMarkAllAsRead}>
              Mark all as read
            </h4>
          </div>

          <div
            className="w-4 h-4 bg-blue-primary [left:-0.5rem] top-[24px] absolute transition transition-transform duration-200 ease-in-out rotate-45"
            ref={arrowRef}
          />
        </div>
      )}
    </div>
  )
}
